import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Skeleton, Typography } from "@mui/material";
import type {
  PromptLibraryCategory,
  PromptLibraryItem,
} from "@shared/apis/sidekick/generated";
import { SvgIcon } from "@shared/components/SvgIcon";
import { useRef, useState } from "react";
import { SwitchTransition, Transition } from "react-transition-group";
import { PriorityCard as WorkflowSurface } from "./ChatFocusView.styles";
import {
  BackRow,
  PanelContainer,
  PanelTitle,
  Row,
  RowIconContainer,
  RowLabel,
  ViewStack,
  WorkflowPanelHeader,
} from "./PromptLibraryPanel.styles";
import { pickRandomItem } from "@shared/utils/pick-random-item";

const TRANSITION_DURATION = 100;

const TRANSITION_STYLES: Record<string, React.CSSProperties> = {
  entering: { opacity: 0 },
  entered: { opacity: 1 },
  exiting: { opacity: 0 },
  exited: { opacity: 0 },
};

type Props = {
  categories: PromptLibraryCategory[];
  onItemClick: (item: PromptLibraryItem) => void;
  disabled?: boolean;
};

export const PromptLibraryPanel = ({
  categories,
  onItemClick,
  disabled = false,
}: Props) => {
  const [selectedCategory, setSelectedCategory] =
    useState<PromptLibraryCategory | null>(null);
  const viewRef = useRef<HTMLDivElement>(null);
  const transitionKey = selectedCategory?.id ?? "root";

  return (
    <PanelContainer $disabled={disabled}>
      <SwitchTransition mode="out-in">
        <Transition
          key={transitionKey}
          nodeRef={viewRef}
          timeout={TRANSITION_DURATION}
        >
          {(state) => (
            <ViewStack
              ref={viewRef}
              style={{
                transition: `opacity ${TRANSITION_DURATION}ms ease`,
                ...TRANSITION_STYLES[state],
              }}
            >
              {selectedCategory ? (
                <WorkflowSurface>
                  <WorkflowPanelHeader>
                    <PanelTitle variant="subtitle1">
                      {selectedCategory.label}
                    </PanelTitle>
                  </WorkflowPanelHeader>
                  <BackRow
                    onClick={() => setSelectedCategory(null)}
                    size="small"
                    variant="text"
                    startIcon={<ArrowBackIcon fontSize="small" />}
                  >
                    <Typography variant="inherit">Back</Typography>
                  </BackRow>
                  {selectedCategory.subcategories
                    .filter((sub) => (sub.items?.length ?? 0) > 0)
                    .map((sub) => (
                      <Row
                        key={sub.id}
                        onClick={() => {
                          const picked = pickRandomItem(sub.items ?? []);
                          if (picked) {
                            onItemClick(picked);
                          }
                        }}
                      >
                        <RowIconContainer>
                          <SvgIcon
                            name={sub.icon || "ElectricBolt"}
                            fontSize="small"
                          />
                        </RowIconContainer>
                        <RowLabel>{sub.label}</RowLabel>
                      </Row>
                    ))}
                </WorkflowSurface>
              ) : (
                <WorkflowSurface>
                  <WorkflowPanelHeader>
                    <PanelTitle variant="subtitle1">
                      What can I help you get done?
                    </PanelTitle>
                  </WorkflowPanelHeader>
                  {categories.map((category) => (
                    <Row
                      key={category.id}
                      onClick={() => setSelectedCategory(category)}
                    >
                      <RowIconContainer>
                        <SvgIcon name={category.icon} fontSize="small" />
                      </RowIconContainer>
                      <RowLabel>{category.label}</RowLabel>
                    </Row>
                  ))}
                </WorkflowSurface>
              )}
            </ViewStack>
          )}
        </Transition>
      </SwitchTransition>
    </PanelContainer>
  );
};

export const PromptLibraryPanelSkeleton = () => (
  <PanelContainer>
    <WorkflowSurface>
      <WorkflowPanelHeader>
        <Skeleton
          variant="rounded"
          width="80%"
          height={24}
          sx={{ mx: "auto" }}
        />
      </WorkflowPanelHeader>
      {Array(5)
        .fill(null)
        .map((_, i) => (
          <Skeleton
            key={i}
            variant="rounded"
            height={52}
            sx={{ borderRadius: "0.625rem", width: "100%" }}
          />
        ))}
    </WorkflowSurface>
  </PanelContainer>
);
